// The app for someone outside InRange: the pages shared with them and nothing else, with
// no navigation but the shared page's own and none of the staff layout's calls. Any other
// URL lands on their first page. docs/Y2026-Q4/design-amazon-overview-sharing.md
import { useContext } from "react";
import { Navigate, Outlet, ScrollRestoration, useLocation } from "react-router-dom";

import { useSharedPages } from "../api/hooks/useShares";
import { UserContext } from "../auth";
import { NewVersionBanner } from "../components/NewVersionBanner";
import { OverviewNav } from "../routes/amazonOverview/OverviewNav";
import { OVERVIEW_PATH } from "../routes/amazonOverview/overviewPages";
import { ViewAsBanner } from "../routes/amazonOverview/ViewAs";
import styles from "./ExternalLayout.module.css";

export const ExternalLayout = () => {
  const { user, logout } = useContext(UserContext);
  const { pathname } = useLocation();
  const shared = useSharedPages();
  const pages = shared.data?.pages ?? [];
  const allowed = pages.flatMap((p) => p.paths);
  const path = pathname.replace(/\/+$/, "");
  // A shared path takes the pages under it too (/amazon-overview/sites/<code>).
  const open = allowed.some((p) => path === p || path.startsWith(`${p}/`));

  // View as keeps its way out on every page, a refusal or a failure included.
  const signedIn = (
    <div className={styles.account}>
      <ViewAsBanner />
      {user?.email}
      {logout && (
        <>
          {" · "}
          <button type="button" className={styles.signOut} onClick={() => logout()}>
            Sign out
          </button>
        </>
      )}
    </div>
  );

  // On every branch: a refused tab holds the shared-pages read at Loading until it reloads.
  const update = <NewVersionBanner offerAvailable={false} />;

  if (shared.isLoading)
    return (
      <main className={styles.main}>
        {update}
        Loading…
      </main>
    );
  if (shared.isError)
    return (
      <main className={styles.main}>
        {update}
        {signedIn}
        <p>Could not load this page. Reload to try again.</p>
      </main>
    );
  if (allowed.length === 0)
    return (
      <main className={styles.main}>
        {update}
        {signedIn}
        <p>This page has not been shared with you.</p>
      </main>
    );
  if (!open) return <Navigate to={allowed[0]} replace />;
  return (
    <>
    {path.startsWith(OVERVIEW_PATH) && <OverviewNav />}
    <main className={styles.main}>
      {update}
      <Outlet />
      <ScrollRestoration />
    </main>
    </>
  );
};
